import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";
import { ALPHA, FONT_SIZE, INK, MOTION, NEUTRAL, SURFACE } from "@/theme/tokens";
import { TOUCH_TARGET_SX } from "../touchTarget";

export type SystemChoiceItem = {
	value: string;
	label: string;
	/**
	 * Rich row body rendered INSTEAD of the plain `label` — for an option that needs more than one line
	 * of text (a person chip plus its metadata, say). `label` stays required and becomes the row's
	 * accessible name, so a composed row is never left announcing whatever its markup happens to contain.
	 * Must be phrasing content (`component="span"`), since the row itself is a `<button>`.
	 */
	content?: ReactNode;
	dataTestId?: string;
};

type Props = {
	items: SystemChoiceItem[];
	onSelect: (value: string) => void;
	/**
	 * The already-chosen row, when the list OUTLIVES the pick — a long, filterable list the operator
	 * scrolls back through, or a step they return to from later in a wizard. Omit it (the default) for a
	 * list that is dismissed by choosing from it, where a selected state would only ever flash.
	 *
	 * Only then is `aria-pressed` set at all: announcing "not pressed" on every row of a list that has no
	 * persistent selection describes a state that does not exist.
	 */
	selectedValue?: string | null;
	/** Disables every row without changing the layout (e.g. while a pick is in flight / the picker expired). */
	disabled?: boolean;
	/** Accessible name for the option group. */
	ariaLabel?: string;
	dataTestId?: string;
};

/**
 * A single-select vertical list of full-width option rows whose labels WRAP — the readable answer for
 * long, end-distinguished choices (e.g. shift labels differing only in their trailing time) that a chip
 * row would overflow and a dropdown would truncate. Selecting a row IS the answer (no confirm step),
 * mirroring `SystemChips`. Rows reuse the same hover/active/focus affordances as the Annie pickup table.
 */
export const SystemChoiceList = ({ items, onSelect, selectedValue, disabled = false, ariaLabel, dataTestId }: Props) => {
	// `undefined` means "this list has no persistent selection" — distinct from `null`, which is a list
	// that tracks one and has not had a pick yet.
	const tracksSelection = selectedValue !== undefined;
	return (
		<Box role="group" aria-label={ariaLabel} data-testid={dataTestId} sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
			{items.map((item, index) => {
				const selected = tracksSelection && item.value === selectedValue;
				return (
					<Box
						component="button"
						type="button"
						// biome-ignore lint/suspicious/noArrayIndexKey: options are positional and have no stable id of their own.
						key={index}
						disabled={disabled}
						data-testid={item.dataTestId}
						aria-label={item.label}
						aria-pressed={tracksSelection ? selected : undefined}
						onClick={() => onSelect(item.value)}
						sx={(theme) => ({
							...TOUCH_TARGET_SX,
							display: "flex",
							alignItems: "center",
							gap: 1,
							width: "100%",
							boxSizing: "border-box",
							px: 1.5,
							py: 1.25,
							textAlign: "start",
							// The selected row is carried by a brand border and wash rather than by the chevron
							// alone: on a list long enough to scroll, the row has to be findable at a glance from
							// anywhere in it, and an 18px glyph at the far end is not.
							border: `1px solid ${selected ? theme.palette.primary.main : NEUTRAL[200]}`,
							borderRadius: 2,
							backgroundColor: selected ? alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY) : SURFACE,
							fontFamily: "inherit",
							fontSize: FONT_SIZE.SECONDARY,
							fontWeight: 600,
							lineHeight: 1.4,
							color: INK.PRIMARY,
							// The long-label failure this list exists to solve: wrap across lines, break unbreakable tokens.
							overflowWrap: "anywhere",
							"&:disabled": { opacity: 0.6, cursor: "default" },
							"&:not(:disabled)": {
								cursor: "pointer",
								transition: `background-color ${MOTION.DURATION.HOVER}ms ease, border-color ${MOTION.DURATION.HOVER}ms ease`,
								"@media (hover: hover) and (pointer: fine)": {
									"&:hover": {
										backgroundColor: selected ? alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER) : NEUTRAL[50],
										borderColor: selected ? theme.palette.primary.dark : NEUTRAL[400],
									},
								},
								"&:active": { backgroundColor: selected ? alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER) : NEUTRAL[150] },
								"&:focus-visible": { outline: `2px solid ${NEUTRAL[400]}`, outlineOffset: "-2px" },
								"@media (prefers-reduced-motion: reduce)": { transition: "none" },
							},
						})}
					>
						<Box component="span" sx={{ flex: 1, minWidth: 0 }}>
							{item.content ?? item.label}
						</Box>
						{selected ? (
							<CheckCircleIcon sx={(theme) => ({ fontSize: 18, color: theme.palette.primary.main, flexShrink: 0 })} />
						) : (
							<ChevronRightIcon sx={{ fontSize: 18, color: INK.MUTED, flexShrink: 0 }} />
						)}
					</Box>
				);
			})}
		</Box>
	);
};
